import { useConnectors } from "@/features/connectors/hooks";
import { useConnections } from "@/features/settings/hooks";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api, withQuery } from "@/lib/api";
import { useApiQuery } from "@/lib/query";
import type { MatchCount, TraceFilterOptions, TracePage } from "./types";
import { toParams, type TraceQuery } from "./trace-query";

/** Connections that are trace sources (Langfuse), not agent platforms. */
export function useTraceSources() {
  const connectors = useConnectors();
  const connections = useConnections();
  const kinds = new Set((connectors.data ?? []).filter((c) => c.kind === "traces").map((c) => c.type));
  return {
    sources: (connections.data ?? []).filter((c) => kinds.has(c.connector_type)),
    isPending: connectors.isPending || connections.isPending,
    isError: connectors.isError || connections.isError,
    error: connectors.error ?? connections.error,
  };
}

/** Each filter button's values, with observation counts under the other filters (last 92 days). */
export const useTraceFilters = (connectionId: string, q: TraceQuery) =>
  useApiQuery<TraceFilterOptions>(`/trace-sources/${connectionId}/filters`, toParams(q), {
    enabled: !!connectionId,
  });

/** How many observations match (last 92 days). */
export const useMatchCount = (connectionId: string, q: TraceQuery) =>
  useApiQuery<MatchCount>(`/trace-sources/${connectionId}/count`, toParams(q), {
    enabled: !!connectionId,
  });

/** The newest matching observations, newest first, to try the evaluator on one. */
export const useRecentTraces = (connectionId: string, q: TraceQuery) =>
  useApiQuery<TracePage>(
    `/trace-sources/${connectionId}/traces`,
    { ...toParams(q), limit: 100 },
    { enabled: !!connectionId },
  );

/** The rows a picker page holds; "Load more" asks for the next this many. */
export const PICKER_PAGE = 20;

/** The same, a page at a time: each page's `next_cursor` fetches the next; null ends it. */
export function useTracePages(connectionId: string, q: TraceQuery) {
  const path = `/trace-sources/${connectionId}/traces`;
  const params = { ...toParams(q), limit: PICKER_PAGE };
  return useInfiniteQuery({
    queryKey: [path, params, "pages"],
    queryFn: ({ pageParam }) =>
      api.get<TracePage>(withQuery(path, pageParam ? { ...params, cursor: pageParam } : params)),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!connectionId,
  });
}
